/* BuiltIn Imports */
import React, { Suspense, useCallback, useEffect, useState } from 'react';

/* External Imports */
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import axios from 'axios';
import parse from 'html-react-parser';
import { isString } from 'lodash';

/* Internal Imports */
/* Components */
import SimpleButton from '@components/Buttons/SimpleButton';
import LazyLoadImageComponent from '@components/UI/LazyLoadImageComponent';
import Loader from '@components/UI/ProgramFinder/Loader';
import { useProgramContext } from '@components/UI/ProgramFinder/ProgramFinderStore';
import config from '@config';
import { CountriesList } from '../../../constants';

/**
 *
 * @param {object} card
 * @returns
 */

const ProgramCard = ({ card, region, lang, data, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [apiStatus, setStatus] = useState(0);
  const [programData, setProgramData] = useState({});
  // const [error, setError] = useState('');

  const countries = CountriesList;

  const { suggestionValues, pushIntoDataLayer } = useProgramContext();

  const onClose = () => {
    setIsOpen(false);
  };

  const getCountryByCode = country => {
    //return if full country name available
    if (country.length > 2 || country.length === 0) {
      return [{ name: country }];
    }

    return countries.filter(item => {
      return country.toLowerCase() === item?.value?.toLowerCase();
    });
  };

  const getFullLanguage = codes => {
    let fullLang = [];

    codes.split(',').map(langCode => {
      suggestionValues.forEach(obj => {
        if (
          obj?.isLanguage &&
          obj?.code?.toLowerCase() == langCode?.toLowerCase()
        ) {
          fullLang.push(obj.name);
        }
      });
    });

    if (fullLang?.length > 13) {
      return [data.json?.IEOLanguages];
    } else {
      return fullLang;
    }
  };

  const getRangedDate = (from, to) => {
    const fromDate = new Date(from.replace(/ /g, 'T')).getDate();
    const fromMonth = new Date(from.replace(/ /g, 'T')).getMonth();
    const fromYear = new Date(from.replace(/ /g, 'T')).getFullYear();
    const toDate = new Date(to.replace(/ /g, 'T')).getDate();
    const toMonth = new Date(to.replace(/ /g, 'T')).getMonth();
    const toYear = new Date(to.replace(/ /g, 'T')).getFullYear();
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'June',
      'July',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    if (toMonth !== fromMonth) {
      return `${fromDate} ${months[fromMonth]} ${fromYear}-${toDate} ${months[toMonth]} ${toYear}`;
    } else if (
      toDate == fromDate &&
      toMonth == fromMonth &&
      toYear == fromYear
    ) {
      return `${toDate} ${months[toMonth]} ${toYear}`;
    } else {
      return `${fromDate}-${toDate} ${months[toMonth]} ${toYear}`;
    }
  };

  const fetchProgramData = useCallback(async () => {
    setLoading(true);
    axios
      .get(
        `${config.SCHEDULE_API_PATH}?option=com_program&v=2&format=json&task=details&program_id=${card?.id}`
      )
      .then(res => {
        setStatus(res?.status);
        // setError(res?.data[0]);

        setProgramData(res?.data[0]);
        setLoading(false);
      })
      .catch(err => {
        setStatus(err?.status);
        // setError('error');
        setProgramData({});
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchProgramData();
    }
    return () => {
      setProgramData();
      // setError();
    };
  }, [isOpen]);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: card.name,
    startDate: card.fr,
    endDate: card.to,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: card.place,
      address: {
        '@type': 'PostalAddress',
        addressCountry: card.cntry,
      },
    },
    inLanguage: card.lang,
    audience: [
      {
        '@type': 'Audience',
        name: 'Gents',
      },
    ],
    organizer: {
      '@type': 'Organization',
      name: 'Isha Foundation',
      url: 'https://www.ishafoundation.org',
    },
    description: card.name,
  };

  // if (card?.flag.includes('inperson' && 'regClosed')) {
  //   console.log('regClosed');
  // }
  return (
    <Box
      width={{ base: '100%', md: '100%', xl: '48%' }}
      m={{ base: '0', md: '0' }}
      // p={{ base: '2px 13px 10px 5px', md: '0' }}
      maxW="330px"
      minW="303px"
      {...props}
    >
      <Flex
        onClick={() => {
          // if (card?.flag.includes('regClosed')) {
          //   setIsOpen(false);
          // } else {
          setIsOpen(true);
          window.dataLayer = window.dataLayer || [];
          pushIntoDataLayer('pf_card_clicks', { program_name: card?.name });
          // }
        }}
        cursor="pointer"
        padding="10px"
        paddingRight="0.8rem"
        width={{ base: 'auto' }}
        // width={{ base: '89vw', md: '360px', lg: '330px' }}

        background="#ffffff"
        boxShadow="0px 0px 12px rgba(0, 0, 0, 0.2), 0px 0px 11px -1px rgba(0, 0, 0, 0.08)"
        fontFamily="FedraSansStd-book"
        minH={{ base: 'auto', md: '163px' }}
        height={'150px'}
      >
        <Flex flexDirection="column">
          <Box mb="4px" w="110px" h="83.37px">
            {card?.flag.includes('regClosed') && (
              <Text
                position="absolute"
                fontFamily="FedraSansStd-medium"
                fontWeight="500"
                fontSize="10px"
                lineHeight="14px"
                alignContent="center"
                textAlign="center"
                noOfLines="2"
                color="#ffffff"
                w="82px"
                h="82px"
                pt="27px"
                objectFit="fill"
                align="top"
                background="rgba(0, 0, 0, 0.67)"
              >
                Registration Closed
              </Text>
            )}
            <Suspense loading="...loading">
              <LazyLoadImageComponent
                src={
                  card.img?.indexOf('http') > -1
                    ? card.img
                    : `${config.staticPath}/d${card.img}`
                }
                w="82px"
                h="82px"
                objectFit="fill"
                align="top"
                loading="lazy"
              />
            </Suspense>
          </Box>

          <Box
            w={{ base: '82px', md: '75px', lg: '82px' }}
            fontSize="12px"
            lineHeight="15px"
            h="24.85px"
            m="4px 0"
            textAlign="center"
            pt="5px"
            background="#F8F1E7"
            borderRadius="63px"
          >
            {card?.flag?.includes('inperson')
              ? 'In-Person'
              : data.json?.onlineProgram}
          </Box>
        </Flex>

        <Flex
          flexDirection="column"
          // gridRowGap="6px"
          pt="0px"
          pos="relative"
          left="-15px"
        >
          <Box
            // width={{ base: '270px', md: '250.7px', lg: '212px' }}
            color="#28231E"
            fontSize="16px"
            lineHeight="20px"
            fontWeight="bold"
            fontFamily="FedraSerifAStdBook"
            className="truncate-text"
            mb="3.5px"
          >
            {card?.name}
          </Box>

          <Flex my="3.5px">
            <Box
              color="#D04723"
              minW="20px"
              w="20px"
              marginRight={{ base: '5px', md: '6px' }}
            >
              <svg
                width="18"
                height="19"
                viewBox="0 0 18 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 4.31474C0.0945602 4.01356 0.149466 3.69235 0.290217 3.41466C0.786113 2.43537 1.60055 1.91221 2.70041 1.85215C3.01111 1.83517 3.32399 1.84954 3.65429 1.84954C3.65429 1.62582 3.65299 1.41908 3.65429 1.21234C3.65778 0.770137 3.94364 0.45981 4.34715 0.458069C4.7511 0.456328 5.04176 0.766655 5.04568 1.20625C5.04742 1.41342 5.04568 1.6206 5.04568 1.83735H8.17924C8.17924 1.60667 8.17183 1.37164 8.18098 1.13748C8.19972 0.658716 8.65073 0.346647 9.09826 0.495935C9.37279 0.587771 9.55886 0.838035 9.56801 1.14183C9.57454 1.36729 9.56931 1.59318 9.56931 1.833H12.7386C12.7386 1.60232 12.7299 1.36685 12.7403 1.13226C12.7643 0.588642 13.3469 0.27962 13.8023 0.566009C14.0141 0.699628 14.1213 0.898534 14.1274 1.14706C14.133 1.37208 14.1287 1.59753 14.1287 1.84692C14.3331 1.84692 14.5278 1.85084 14.7226 1.84605C15.1601 1.83604 15.5898 1.86912 16.0059 2.02276C17.0444 2.40621 17.7939 3.40988 17.8109 4.51757C17.8318 5.87247 17.8204 7.22825 17.8165 8.58316C17.8152 8.98402 17.508 9.28216 17.1237 9.28259C16.7328 9.28259 16.4299 8.97966 16.4286 8.56705C16.4247 7.28135 16.4278 5.99565 16.4265 4.70951C16.426 4.1141 16.1816 3.65492 15.6395 3.38637C15.4826 3.3089 15.2991 3.26102 15.1244 3.24927C14.8019 3.22795 14.4769 3.24318 14.13 3.24318C14.13 3.45297 14.1313 3.65927 14.13 3.86514C14.1269 4.31692 13.8363 4.63029 13.4262 4.62551C13.0232 4.62072 12.7417 4.31083 12.7386 3.86645C12.7373 3.66449 12.7386 3.46254 12.7386 3.2484H9.57149C9.57149 3.43947 9.57149 3.6288 9.57149 3.81813C9.57062 4.30647 9.29958 4.6229 8.8795 4.62551C8.45856 4.62812 8.18054 4.31083 8.17924 3.8264C8.17924 3.6362 8.17924 3.44644 8.17924 3.24666H5.04524C5.04524 3.47603 5.05265 3.71063 5.0435 3.94479C5.0252 4.42399 4.57506 4.73649 4.12709 4.58764C3.84646 4.4945 3.66257 4.24032 3.6556 3.9252C3.65081 3.70105 3.65473 3.4769 3.65473 3.21794C3.24424 3.23796 2.84465 3.21402 2.46336 3.28627C1.84109 3.40378 1.42973 3.94479 1.39444 4.59635C1.39182 4.64248 1.39226 4.68905 1.39226 4.73519C1.39226 8.27979 1.39226 11.8244 1.39226 15.369C1.39226 15.9709 1.6267 16.4458 2.18142 16.7061C2.3932 16.8053 2.64943 16.851 2.88561 16.8527C4.59641 16.8649 6.30764 16.8601 8.01844 16.858C8.24329 16.858 8.44461 16.9063 8.60541 17.0695C8.94007 17.409 8.83724 17.9744 8.4054 18.1802C8.35398 18.205 8.30081 18.2264 8.24852 18.2494H2.47077C2.44506 18.239 2.42022 18.2225 2.3932 18.2181C1.41448 18.0605 0.700704 17.5365 0.274094 16.6477C0.144237 16.3775 0.089331 16.0715 0 15.7821C0 11.9602 0 8.13747 0 4.31474Z"
                  fill="#CF4520"
                />
                <path
                  d="M13.2619 18.2503C12.8645 18.1519 12.454 18.0888 12.0718 17.9491C10.2652 17.2888 9.11998 15.2902 9.45726 13.3995C9.75445 11.7321 10.7096 10.58 12.3145 10.0599C13.9652 9.52497 15.4573 9.91408 16.6582 11.1676C17.7637 12.3214 18.0975 13.7281 17.6304 15.2501C17.1323 16.8745 15.9928 17.852 14.3147 18.1841C14.1962 18.2076 14.0768 18.2281 13.9578 18.2503H13.2619ZM16.4281 14.0419C16.426 12.4924 15.1527 11.2259 13.6022 11.2307C12.057 11.2355 10.795 12.4964 10.7911 14.0397C10.7876 15.5883 12.0566 16.8588 13.6083 16.8597C15.1596 16.8605 16.4299 15.5909 16.4281 14.0423V14.0419Z"
                  fill="#CF4520"
                />
                <path
                  d="M5.04553 7.75607C5.04466 8.13821 4.72743 8.45158 4.34483 8.4481C3.96223 8.44462 3.65066 8.12602 3.65633 7.74388C3.66199 7.3687 3.97269 7.06186 4.34875 7.06055C4.73135 7.05925 5.04728 7.37392 5.04597 7.75607H5.04553Z"
                  fill="#CF4520"
                />
                <path
                  d="M7.38299 8.44811C7.00039 8.45072 6.68359 8.13691 6.68359 7.75477C6.68359 7.37263 6.99996 7.05838 7.38255 7.06056C7.75818 7.06273 8.06844 7.37001 8.07324 7.74519C8.07803 8.12734 7.76559 8.4455 7.38299 8.44811Z"
                  fill="#CF4520"
                />
                <path
                  d="M11.1003 7.7578C11.0985 8.13995 10.78 8.45245 10.3978 8.4481C10.0152 8.44374 9.70408 8.12428 9.71105 7.74214C9.71759 7.36696 10.0292 7.06098 10.4048 7.06055C10.7874 7.06011 11.1024 7.37566 11.1003 7.7578Z"
                  fill="#CF4520"
                />
                <path
                  d="M13.4338 8.44814C13.0512 8.44858 12.7361 8.13303 12.7383 7.75089C12.74 7.36874 13.0586 7.05624 13.4407 7.06059C13.8164 7.06494 14.1249 7.37397 14.1275 7.74914C14.1305 8.13129 13.8164 8.44771 13.4338 8.44814Z"
                  fill="#CF4520"
                />
                <path
                  d="M4.34397 10.084C4.72657 10.0801 5.04424 10.393 5.04554 10.7752C5.04685 11.1573 4.7318 11.4724 4.3492 11.4716C3.97314 11.4707 3.66244 11.1643 3.65634 10.7891C3.65024 10.4074 3.96181 10.0879 4.3444 10.084H4.34397Z"
                  fill="#CF4520"
                />
                <path
                  d="M7.38255 11.4716C6.99996 11.4737 6.68359 11.1595 6.68359 10.7773C6.68359 10.3952 7.00039 10.0814 7.38299 10.084C7.75862 10.0866 8.06844 10.3943 8.07324 10.7695C8.07803 11.1516 7.76515 11.4694 7.38255 11.4716Z"
                  fill="#CF4520"
                />
                <path
                  d="M5.04583 13.8176C5.03756 14.1993 4.71422 14.507 4.33206 14.4966C3.94946 14.4861 3.64399 14.1614 3.65663 13.7797C3.66927 13.4045 3.98563 13.1038 4.36125 13.1095C4.74385 13.1151 5.05368 13.4363 5.04583 13.818V13.8176Z"
                  fill="#CF4520"
                />
                <path
                  d="M7.37908 14.497C6.99648 14.4974 6.68143 14.1819 6.6836 13.7997C6.68535 13.4176 7.00389 13.1051 7.38605 13.1094C7.76168 13.1138 8.0702 13.4228 8.07281 13.798C8.07586 14.1801 7.76168 14.4965 7.37908 14.497Z"
                  fill="#CF4520"
                />
                <path
                  d="M14.3002 13.354C14.4379 13.354 14.5477 13.3479 14.6571 13.3553C15.0323 13.381 15.316 13.6879 15.3103 14.0569C15.3051 14.4191 15.0231 14.7229 14.654 14.7363C14.2897 14.7498 13.9241 14.7503 13.5598 14.7355C13.2047 14.7211 12.9249 14.4391 12.9158 14.0831C12.9031 13.586 12.9031 13.0881 12.9153 12.5915C12.9245 12.2146 13.2356 11.9286 13.6056 11.9277C13.9751 11.9273 14.2815 12.2128 14.2985 12.5893C14.3098 12.8361 14.3006 13.0842 14.3006 13.354H14.3002Z"
                  fill="#CF4520"
                />
              </svg>
            </Box>
            <Box
              fontWeight="bold"
              fontSize="14px"
              lineHeight="18px"
              color="#D04723"
            >
              {card?.fr && card?.to
                ? getRangedDate(card?.fr, card?.to)
                : isString(card?.fr)
                ? card.fr
                : null}
            </Box>
          </Flex>

          <Flex my="3.5px">
            <Box
              color="#676055"
              minW="20px"
              w="20px"
              marginRight={{ base: '5px', md: '6px' }}
            >
              <svg
                width="15"
                height="21"
                viewBox="0 0 15 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.9667 20.8334C6.81439 20.6868 6.63353 20.5596 6.51349 20.3903C5.3178 18.6951 4.11313 17.0057 2.9497 15.289C1.98776 13.8693 1.14798 12.3775 0.570492 10.7549C0.20983 9.74147 -0.0165094 8.70334 0.000942005 7.62053C0.0501232 4.57869 2.14641 1.84276 5.08882 0.985445C8.98577 -0.149926 12.9943 2.10347 14.0245 6.00841C14.4169 7.49648 14.2661 8.95722 13.7992 10.4006C13.2889 11.9775 12.521 13.433 11.6072 14.807C10.5458 16.4023 9.42893 17.9608 8.33637 19.5356C8.13806 19.8216 7.95138 20.1165 7.7372 20.3898C7.60817 20.5548 7.4363 20.6862 7.28347 20.8329H6.9667V20.8334ZM7.11953 19.1845C7.15443 19.1446 7.17294 19.1272 7.18775 19.1062C8.28137 17.5398 9.39614 15.9871 10.4612 14.4012C11.3629 13.0588 12.1387 11.6437 12.6495 10.101C13.0366 8.93094 13.2074 7.73985 12.9271 6.52038C12.0715 2.79783 8.12907 0.850883 4.62768 2.4178C2.26434 3.47538 0.851301 6.09619 1.25215 8.64815C1.4235 9.74094 1.79844 10.7628 2.27491 11.7531C3.06287 13.3904 4.07347 14.8937 5.11843 16.3755C5.77683 17.3085 6.44368 18.2357 7.12006 19.1845H7.11953Z"
                  fill="#676055"
                />
                <path
                  d="M7.1427 4.22853C9.10255 4.23484 10.6949 5.83277 10.6875 7.78656C10.6806 9.73456 9.07241 11.3172 7.10727 11.3104C5.14743 11.3036 3.55565 9.70565 3.56252 7.75186C3.56887 5.80439 5.17704 4.22223 7.1427 4.22853ZM7.12525 5.40911C5.81851 5.40911 4.74816 6.47405 4.74975 7.77394C4.75133 9.06332 5.81798 10.1267 7.11415 10.1304C8.423 10.134 9.49759 9.07331 9.49917 7.77499C9.50129 6.47457 8.43199 5.40964 7.12472 5.40911H7.12525Z"
                  fill="#676055"
                />
              </svg>
            </Box>
            <Box
              fontSize="14px"
              lineHeight="16.5px"
              color="#676055"
              className="truncate-text"
            >
              {card?.flag?.toLowerCase()?.includes('online')
                ? data?.json?.onlineProgram
                : (card?.place ? `${card.place}, ` : '') +
                  (card?.city ? `${card.city}, ` : '') +
                  //(card?.state ? getStateByCode(card.state)[0]?.name : '') +
                  (card?.cntry ? getCountryByCode(card.cntry)[0]?.name : '')}
            </Box>
          </Flex>

          {card?.lang &&
            isNaN(card.lang) &&
            card?.lang?.toLowerCase() !== 'unspecified' && (
              <Flex my="3.5px">
                <Box
                  color="#676055"
                  minW="20px"
                  w="20px"
                  marginRight={{ base: '5px', md: '6px' }}
                >
                  <svg
                    width="20"
                    height="19"
                    viewBox="0 0 20 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.27773 10.8513C6.53501 10.8513 5.81901 10.8484 5.10359 10.8564C5.04217 10.857 4.96483 10.9175 4.92388 10.9728C4.59915 11.409 4.28182 11.8504 3.96164 12.2901C3.68696 12.6675 3.31787 12.807 2.92433 12.6825C2.516 12.5534 2.29023 12.2261 2.28568 11.7479C2.28284 11.4609 2.28568 11.1739 2.28568 10.8697C2.21516 10.857 2.15601 10.8426 2.09573 10.8357C0.867909 10.6939 0.021114 9.77255 0.0108774 8.51926C-0.00390877 6.7001 -0.00334007 4.88095 0.0108774 3.06237C0.021114 1.70131 1.01293 0.725759 2.35961 0.724607C5.03535 0.722302 7.71051 0.721726 10.3862 0.724607C11.7403 0.726336 12.7225 1.72091 12.7276 3.0906C12.731 3.98203 12.7282 4.87346 12.7282 5.76546C12.7282 5.84267 12.7282 5.91931 12.7282 6.01957C12.8339 6.01957 12.9175 6.01957 13.0006 6.01957C14.5548 6.01957 16.1091 6.01496 17.6633 6.0213C18.811 6.02591 19.6999 6.73756 19.9398 7.83873C19.9808 8.02658 19.9956 8.22365 19.9961 8.41669C20.0001 10.1926 20.0018 11.9686 19.9973 13.7445C19.9939 14.9707 19.233 15.9042 18.0819 16.1088C17.9716 16.1284 17.859 16.1341 17.7202 16.1491C17.7202 16.4338 17.714 16.7121 17.7213 16.9898C17.7333 17.4283 17.5712 17.7614 17.1657 17.9435C16.7938 18.1106 16.4349 18.0138 16.0903 17.6652C15.6331 17.203 15.1815 16.7351 14.7163 16.2816C14.6356 16.2033 14.4991 16.1433 14.3882 16.1428C12.7987 16.1341 11.2086 16.1405 9.61907 16.1353C8.48565 16.1318 7.59677 15.4415 7.34427 14.3691C7.29707 14.1686 7.28171 13.9571 7.28 13.7503C7.27318 12.8732 7.27716 11.9962 7.27716 11.1186V10.8524L7.27773 10.8513ZM16.7631 17.0716C16.7807 17.0618 16.7983 17.0515 16.816 17.0417V16.0166C16.816 15.2911 16.8871 15.2196 17.6093 15.2185C18.5226 15.2173 19.0817 14.6544 19.0828 13.7284C19.0845 11.9599 19.0845 10.1915 19.0828 8.42303C19.0817 7.4976 18.5226 6.93635 17.607 6.93578C14.9603 6.93578 12.313 6.93578 9.66627 6.93578C8.74668 6.93578 8.1831 7.50797 8.18253 8.44319C8.1814 10.1972 8.1814 11.9513 8.18253 13.7053C8.1831 14.6521 8.74555 15.2179 9.68333 15.2179C11.3365 15.2179 12.9903 15.2208 14.6435 15.215C14.8665 15.2145 15.0365 15.2848 15.1901 15.4467C15.5046 15.7786 15.8321 16.0972 16.1512 16.4251C16.3582 16.6377 16.5595 16.8561 16.7631 17.0716ZM11.8086 6.01957C11.8165 5.95273 11.8228 5.9245 11.8228 5.89626C11.8234 4.94721 11.8256 3.99816 11.8205 3.04911C11.8199 2.91428 11.7978 2.77598 11.7619 2.64575C11.5879 2.01882 11.0641 1.64196 10.3652 1.64139C8.00908 1.63966 5.65295 1.64081 3.29626 1.64081C2.96243 1.64081 2.62917 1.6339 2.29535 1.64254C1.52305 1.66328 0.933309 2.2205 0.92876 2.97709C0.916817 4.85329 0.919092 6.73007 0.92876 8.60684C0.932172 9.21534 1.34562 9.72818 1.93195 9.87051C2.14862 9.92295 2.38122 9.91661 2.60699 9.9218C3.02613 9.93102 3.18878 10.0889 3.18934 10.5078C3.18991 10.9146 3.18934 11.3209 3.18934 11.7813C3.26953 11.6799 3.31787 11.6222 3.36166 11.5617C3.69037 11.1111 4.02363 10.6634 4.34437 10.2065C4.48598 10.0054 4.66171 9.91892 4.90284 9.92064C5.61258 9.92698 6.32231 9.92295 7.03205 9.92295C7.10769 9.92295 7.18333 9.92295 7.27716 9.92295C7.27716 9.43834 7.28854 8.97966 7.27375 8.52156C7.25839 8.05597 7.35962 7.61803 7.57175 7.17376C7.45062 7.16857 7.38123 7.16281 7.31242 7.16281C6.6806 7.16224 6.04934 7.16742 5.41751 7.15878C5.26737 7.15647 5.19174 7.20372 5.13202 7.34433C4.93582 7.80588 4.72427 8.26053 4.51726 8.7169C4.43252 8.90418 4.2875 9.01078 4.08163 9.00214C3.88657 8.99407 3.72222 8.89265 3.68411 8.70307C3.65511 8.56132 3.67899 8.38557 3.73814 8.25189C4.45413 6.63383 5.18321 5.02212 5.90318 3.4058C5.99815 3.1926 6.12099 3.02664 6.36667 3.02607C6.61235 3.02491 6.7386 3.18914 6.83301 3.40234C7.25385 4.35197 7.68151 5.29814 8.10746 6.24546C8.13874 6.31518 8.17514 6.38202 8.21552 6.46385C8.71029 6.09276 9.25738 5.99883 9.83973 6.01554C10.3857 6.0311 10.9328 6.019 11.4793 6.019H11.8074L11.8086 6.01957ZM6.36895 4.59687C6.11303 5.17021 5.87816 5.69516 5.63817 6.23336H7.10428C6.85917 5.68824 6.62316 5.16272 6.36895 4.59687Z"
                      fill="#676055"
                    />
                    <path
                      d="M13.204 13.2162C12.9271 12.7926 12.6449 12.3914 12.3979 11.9695C12.2621 11.7371 12.3726 11.4633 12.5955 11.3471C12.8289 11.2261 13.0893 11.3123 13.2322 11.5595C13.2687 11.6232 13.2928 11.6952 13.3357 11.7536C13.5685 12.0692 13.806 12.3819 14.053 12.7105C14.7267 12.1359 15.093 11.4645 15.2359 10.6296H14.9878C13.9536 10.6296 12.9194 10.6296 11.8852 10.6296C11.8047 10.6296 11.7218 10.6326 11.6448 10.6149C11.4196 10.5642 11.265 10.3494 11.2826 10.1264C11.3014 9.88743 11.4819 9.70158 11.7312 9.69686C12.2662 9.68742 12.8018 9.69214 13.3374 9.69155C13.4239 9.69155 13.5109 9.69155 13.6332 9.69155C13.6332 9.36647 13.6273 9.06026 13.6349 8.75464C13.642 8.47322 13.8225 8.28914 14.0818 8.28147C14.3587 8.2738 14.5592 8.4685 14.5656 8.76644C14.5721 9.06616 14.5668 9.36647 14.5668 9.69155C14.8866 9.69155 15.1853 9.69155 15.484 9.69155C15.7991 9.69155 16.1148 9.68683 16.43 9.69391C16.7228 9.70099 16.9079 9.88802 16.9079 10.1588C16.9079 10.4267 16.7169 10.6161 16.43 10.6291C16.3571 10.6326 16.2842 10.6291 16.1877 10.6291C16.0572 11.6114 15.6568 12.4592 14.9443 13.1796C15.3064 13.3625 15.6762 13.4215 16.0531 13.4528C16.126 13.4587 16.2007 13.4516 16.273 13.4622C16.5311 13.4988 16.7039 13.7135 16.6887 13.9725C16.674 14.2227 16.4893 14.3908 16.2165 14.392C15.5363 14.3944 14.8943 14.2327 14.2946 13.9147C14.1535 13.8398 14.053 13.8445 13.9154 13.9183C13.3298 14.2304 12.7025 14.3908 12.0375 14.392C11.7177 14.3926 11.4943 14.1938 11.4966 13.9112C11.4984 13.6457 11.7024 13.4622 12.0122 13.4557C12.4338 13.4469 12.8377 13.3601 13.2028 13.2156L13.204 13.2162Z"
                      fill="#676055"
                    />
                  </svg>
                </Box>
                <Box fontSize="12px" lineHeight="18px" color="#676055">
                  <Box display="inline" mr="4px" textTransform="capitalize">
                    {getFullLanguage(card?.lang)?.join(', ')}
                  </Box>
                </Box>
              </Flex>
            )}
        </Flex>

        {schemaData?.name && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
          />
        )}
      </Flex>
      <Modal
        isCentered={{ base: 'false', md: 'true' }}
        isOpen={isOpen}
        onClose={onClose}
        size="2xl"
        width="100%"
      >
        <ModalOverlay />
        <ModalContent
          position={{ base: 'absolute', md: 'relative' }}
          bottom={{ base: '0', md: '0' }}
          fontFamily="FedraSansStd-book"
          borderRadius="0"
          mb={{ base: '0', md: '3.75rem' }}
          backgroundColor={'white'}
        >
          {!loading && (
            <ModalCloseButton
              height="20px"
              width="20px"
              color="#726C6C"
              mr="20px"
              mt="16px"
            />
          )}
          {!loading && apiStatus === 200 && (
            <>
              <ModalBody
                maxH="80vh"
                overflowY="auto"
                pl={{ base: '20px', md: '38px' }}
              >
                {programData?.title && (
                  <Flex flexDir="column" mt="25px">
                    <Flex my="4.2222px">
                      <Box
                        as="h2"
                        fontSize="24px"
                        fontWeight="600"
                        maxWidth={{ base: '95%', md: '100%' }}
                        mb="8px"
                        pr={{ base: '1rem', md: 'none' }}
                        lineHeight="30px"
                        color={{ base: '#28231E' }}
                      >
                        {programData.title}
                      </Box>
                    </Flex>
                    {programData.date && (
                      <Flex align="center" color="#CF4520" my="4.2222px">
                        <Box
                          display={{ base: 'flex', md: 'none' }}
                          width="20px"
                          minW="20px"
                          alignItems="start"
                        >
                          <svg
                            width="18"
                            height="19"
                            viewBox="0 0 18 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M0 4.31474C0.0945602 4.01356 0.149466 3.69235 0.290217 3.41466C0.786113 2.43537 1.60055 1.91221 2.70041 1.85215C3.01111 1.83517 3.32399 1.84954 3.65429 1.84954C3.65429 1.62582 3.65299 1.41908 3.65429 1.21234C3.65778 0.770137 3.94364 0.45981 4.34715 0.458069C4.7511 0.456328 5.04176 0.766655 5.04568 1.20625C5.04742 1.41342 5.04568 1.6206 5.04568 1.83735H8.17924C8.17924 1.60667 8.17183 1.37164 8.18098 1.13748C8.19972 0.658716 8.65073 0.346647 9.09826 0.495935C9.37279 0.587771 9.55886 0.838035 9.56801 1.14183C9.57454 1.36729 9.56931 1.59318 9.56931 1.833H12.7386C12.7386 1.60232 12.7299 1.36685 12.7403 1.13226C12.7643 0.588642 13.3469 0.27962 13.8023 0.566009C14.0141 0.699628 14.1213 0.898534 14.1274 1.14706C14.133 1.37208 14.1287 1.59753 14.1287 1.84692C14.3331 1.84692 14.5278 1.85084 14.7226 1.84605C15.1601 1.83604 15.5898 1.86912 16.0059 2.02276C17.0444 2.40621 17.7939 3.40988 17.8109 4.51757C17.8318 5.87247 17.8204 7.22825 17.8165 8.58316C17.8152 8.98402 17.508 9.28216 17.1237 9.28259C16.7328 9.28259 16.4299 8.97966 16.4286 8.56705C16.4247 7.28135 16.4278 5.99565 16.4265 4.70951C16.426 4.1141 16.1816 3.65492 15.6395 3.38637C15.4826 3.3089 15.2991 3.26102 15.1244 3.24927C14.8019 3.22795 14.4769 3.24318 14.13 3.24318C14.13 3.45297 14.1313 3.65927 14.13 3.86514C14.1269 4.31692 13.8363 4.63029 13.4262 4.62551C13.0232 4.62072 12.7417 4.31083 12.7386 3.86645C12.7373 3.66449 12.7386 3.46254 12.7386 3.2484H9.57149C9.57149 3.43947 9.57149 3.6288 9.57149 3.81813C9.57062 4.30647 9.29958 4.6229 8.8795 4.62551C8.45856 4.62812 8.18054 4.31083 8.17924 3.8264C8.17924 3.6362 8.17924 3.44644 8.17924 3.24666H5.04524C5.04524 3.47603 5.05265 3.71063 5.0435 3.94479C5.0252 4.42399 4.57506 4.73649 4.12709 4.58764C3.84646 4.4945 3.66257 4.24032 3.6556 3.9252C3.65081 3.70105 3.65473 3.4769 3.65473 3.21794C3.24424 3.23796 2.84465 3.21402 2.46336 3.28627C1.84109 3.40378 1.42973 3.94479 1.39444 4.59635C1.39182 4.64248 1.39226 4.68905 1.39226 4.73519C1.39226 8.27979 1.39226 11.8244 1.39226 15.369C1.39226 15.9709 1.6267 16.4458 2.18142 16.7061C2.3932 16.8053 2.64943 16.851 2.88561 16.8527C4.59641 16.8649 6.30764 16.8601 8.01844 16.858C8.24329 16.858 8.44461 16.9063 8.60541 17.0695C8.94007 17.409 8.83724 17.9744 8.4054 18.1802C8.35398 18.205 8.30081 18.2264 8.24852 18.2494H2.47077C2.44506 18.239 2.42022 18.2225 2.3932 18.2181C1.41448 18.0605 0.700704 17.5365 0.274094 16.6477C0.144237 16.3775 0.089331 16.0715 0 15.7821C0 11.9602 0 8.13747 0 4.31474Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M13.2619 18.2503C12.8645 18.1519 12.454 18.0888 12.0718 17.9491C10.2652 17.2888 9.11998 15.2902 9.45726 13.3995C9.75445 11.7321 10.7096 10.58 12.3145 10.0599C13.9652 9.52497 15.4573 9.91408 16.6582 11.1676C17.7637 12.3214 18.0975 13.7281 17.6304 15.2501C17.1323 16.8745 15.9928 17.852 14.3147 18.1841C14.1962 18.2076 14.0768 18.2281 13.9578 18.2503H13.2619ZM16.4281 14.0419C16.426 12.4924 15.1527 11.2259 13.6022 11.2307C12.057 11.2355 10.795 12.4964 10.7911 14.0397C10.7876 15.5883 12.0566 16.8588 13.6083 16.8597C15.1596 16.8605 16.4299 15.5909 16.4281 14.0423V14.0419Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M5.04553 7.75607C5.04466 8.13821 4.72743 8.45158 4.34483 8.4481C3.96223 8.44462 3.65066 8.12602 3.65633 7.74388C3.66199 7.3687 3.97269 7.06186 4.34875 7.06055C4.73135 7.05925 5.04728 7.37392 5.04597 7.75607H5.04553Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M7.38299 8.44811C7.00039 8.45072 6.68359 8.13691 6.68359 7.75477C6.68359 7.37263 6.99996 7.05838 7.38255 7.06056C7.75818 7.06273 8.06844 7.37001 8.07324 7.74519C8.07803 8.12734 7.76559 8.4455 7.38299 8.44811Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M11.1003 7.7578C11.0985 8.13995 10.78 8.45245 10.3978 8.4481C10.0152 8.44374 9.70408 8.12428 9.71105 7.74214C9.71759 7.36696 10.0292 7.06098 10.4048 7.06055C10.7874 7.06011 11.1024 7.37566 11.1003 7.7578Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M13.4338 8.44814C13.0512 8.44858 12.7361 8.13303 12.7383 7.75089C12.74 7.36874 13.0586 7.05624 13.4407 7.06059C13.8164 7.06494 14.1249 7.37397 14.1275 7.74914C14.1305 8.13129 13.8164 8.44771 13.4338 8.44814Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M4.34397 10.084C4.72657 10.0801 5.04424 10.393 5.04554 10.7752C5.04685 11.1573 4.7318 11.4724 4.3492 11.4716C3.97314 11.4707 3.66244 11.1643 3.65634 10.7891C3.65024 10.4074 3.96181 10.0879 4.3444 10.084H4.34397Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M7.38255 11.4716C6.99996 11.4737 6.68359 11.1595 6.68359 10.7773C6.68359 10.3952 7.00039 10.0814 7.38299 10.084C7.75862 10.0866 8.06844 10.3943 8.07324 10.7695C8.07803 11.1516 7.76515 11.4694 7.38255 11.4716Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M5.04583 13.8176C5.03756 14.1993 4.71422 14.507 4.33206 14.4966C3.94946 14.4861 3.64399 14.1614 3.65663 13.7797C3.66927 13.4045 3.98563 13.1038 4.36125 13.1095C4.74385 13.1151 5.05368 13.4363 5.04583 13.818V13.8176Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M7.37908 14.497C6.99648 14.4974 6.68143 14.1819 6.6836 13.7997C6.68535 13.4176 7.00389 13.1051 7.38605 13.1094C7.76168 13.1138 8.0702 13.4228 8.07281 13.798C8.07586 14.1801 7.76168 14.4965 7.37908 14.497Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M14.3002 13.354C14.4379 13.354 14.5477 13.3479 14.6571 13.3553C15.0323 13.381 15.316 13.6879 15.3103 14.0569C15.3051 14.4191 15.0231 14.7229 14.654 14.7363C14.2897 14.7498 13.9241 14.7503 13.5598 14.7355C13.2047 14.7211 12.9249 14.4391 12.9158 14.0831C12.9031 13.586 12.9031 13.0881 12.9153 12.5915C12.9245 12.2146 13.2356 11.9286 13.6056 11.9277C13.9751 11.9273 14.2815 12.2128 14.2985 12.5893C14.3098 12.8361 14.3006 13.0842 14.3006 13.354H14.3002Z"
                              fill="#CF4520"
                            />
                          </svg>
                        </Box>
                        <Box
                          display={{ base: 'none', md: 'flex' }}
                          width="20px"
                          minW="20px"
                          alignItems="start"
                        >
                          <svg
                            width="18"
                            height="19"
                            viewBox="0 0 18 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M0 4.31474C0.0945602 4.01356 0.149466 3.69235 0.290217 3.41466C0.786113 2.43537 1.60055 1.91221 2.70041 1.85215C3.01111 1.83517 3.32399 1.84954 3.65429 1.84954C3.65429 1.62582 3.65299 1.41908 3.65429 1.21234C3.65778 0.770137 3.94364 0.45981 4.34715 0.458069C4.7511 0.456328 5.04176 0.766655 5.04568 1.20625C5.04742 1.41342 5.04568 1.6206 5.04568 1.83735H8.17924C8.17924 1.60667 8.17183 1.37164 8.18098 1.13748C8.19972 0.658716 8.65073 0.346647 9.09826 0.495935C9.37279 0.587771 9.55886 0.838035 9.56801 1.14183C9.57454 1.36729 9.56931 1.59318 9.56931 1.833H12.7386C12.7386 1.60232 12.7299 1.36685 12.7403 1.13226C12.7643 0.588642 13.3469 0.27962 13.8023 0.566009C14.0141 0.699628 14.1213 0.898534 14.1274 1.14706C14.133 1.37208 14.1287 1.59753 14.1287 1.84692C14.3331 1.84692 14.5278 1.85084 14.7226 1.84605C15.1601 1.83604 15.5898 1.86912 16.0059 2.02276C17.0444 2.40621 17.7939 3.40988 17.8109 4.51757C17.8318 5.87247 17.8204 7.22825 17.8165 8.58316C17.8152 8.98402 17.508 9.28216 17.1237 9.28259C16.7328 9.28259 16.4299 8.97966 16.4286 8.56705C16.4247 7.28135 16.4278 5.99565 16.4265 4.70951C16.426 4.1141 16.1816 3.65492 15.6395 3.38637C15.4826 3.3089 15.2991 3.26102 15.1244 3.24927C14.8019 3.22795 14.4769 3.24318 14.13 3.24318C14.13 3.45297 14.1313 3.65927 14.13 3.86514C14.1269 4.31692 13.8363 4.63029 13.4262 4.62551C13.0232 4.62072 12.7417 4.31083 12.7386 3.86645C12.7373 3.66449 12.7386 3.46254 12.7386 3.2484H9.57149C9.57149 3.43947 9.57149 3.6288 9.57149 3.81813C9.57062 4.30647 9.29958 4.6229 8.8795 4.62551C8.45856 4.62812 8.18054 4.31083 8.17924 3.8264C8.17924 3.6362 8.17924 3.44644 8.17924 3.24666H5.04524C5.04524 3.47603 5.05265 3.71063 5.0435 3.94479C5.0252 4.42399 4.57506 4.73649 4.12709 4.58764C3.84646 4.4945 3.66257 4.24032 3.6556 3.9252C3.65081 3.70105 3.65473 3.4769 3.65473 3.21794C3.24424 3.23796 2.84465 3.21402 2.46336 3.28627C1.84109 3.40378 1.42973 3.94479 1.39444 4.59635C1.39182 4.64248 1.39226 4.68905 1.39226 4.73519C1.39226 8.27979 1.39226 11.8244 1.39226 15.369C1.39226 15.9709 1.6267 16.4458 2.18142 16.7061C2.3932 16.8053 2.64943 16.851 2.88561 16.8527C4.59641 16.8649 6.30764 16.8601 8.01844 16.858C8.24329 16.858 8.44461 16.9063 8.60541 17.0695C8.94007 17.409 8.83724 17.9744 8.4054 18.1802C8.35398 18.205 8.30081 18.2264 8.24852 18.2494H2.47077C2.44506 18.239 2.42022 18.2225 2.3932 18.2181C1.41448 18.0605 0.700704 17.5365 0.274094 16.6477C0.144237 16.3775 0.089331 16.0715 0 15.7821C0 11.9602 0 8.13747 0 4.31474Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M13.2619 18.2503C12.8645 18.1519 12.454 18.0888 12.0718 17.9491C10.2652 17.2888 9.11998 15.2902 9.45726 13.3995C9.75445 11.7321 10.7096 10.58 12.3145 10.0599C13.9652 9.52497 15.4573 9.91408 16.6582 11.1676C17.7637 12.3214 18.0975 13.7281 17.6304 15.2501C17.1323 16.8745 15.9928 17.852 14.3147 18.1841C14.1962 18.2076 14.0768 18.2281 13.9578 18.2503H13.2619ZM16.4281 14.0419C16.426 12.4924 15.1527 11.2259 13.6022 11.2307C12.057 11.2355 10.795 12.4964 10.7911 14.0397C10.7876 15.5883 12.0566 16.8588 13.6083 16.8597C15.1596 16.8605 16.4299 15.5909 16.4281 14.0423V14.0419Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M5.04553 7.75607C5.04466 8.13821 4.72743 8.45158 4.34483 8.4481C3.96223 8.44462 3.65066 8.12602 3.65633 7.74388C3.66199 7.3687 3.97269 7.06186 4.34875 7.06055C4.73135 7.05925 5.04728 7.37392 5.04597 7.75607H5.04553Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M7.38299 8.44811C7.00039 8.45072 6.68359 8.13691 6.68359 7.75477C6.68359 7.37263 6.99996 7.05838 7.38255 7.06056C7.75818 7.06273 8.06844 7.37001 8.07324 7.74519C8.07803 8.12734 7.76559 8.4455 7.38299 8.44811Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M11.1003 7.7578C11.0985 8.13995 10.78 8.45245 10.3978 8.4481C10.0152 8.44374 9.70408 8.12428 9.71105 7.74214C9.71759 7.36696 10.0292 7.06098 10.4048 7.06055C10.7874 7.06011 11.1024 7.37566 11.1003 7.7578Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M13.4338 8.44814C13.0512 8.44858 12.7361 8.13303 12.7383 7.75089C12.74 7.36874 13.0586 7.05624 13.4407 7.06059C13.8164 7.06494 14.1249 7.37397 14.1275 7.74914C14.1305 8.13129 13.8164 8.44771 13.4338 8.44814Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M4.34397 10.084C4.72657 10.0801 5.04424 10.393 5.04554 10.7752C5.04685 11.1573 4.7318 11.4724 4.3492 11.4716C3.97314 11.4707 3.66244 11.1643 3.65634 10.7891C3.65024 10.4074 3.96181 10.0879 4.3444 10.084H4.34397Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M7.38255 11.4716C6.99996 11.4737 6.68359 11.1595 6.68359 10.7773C6.68359 10.3952 7.00039 10.0814 7.38299 10.084C7.75862 10.0866 8.06844 10.3943 8.07324 10.7695C8.07803 11.1516 7.76515 11.4694 7.38255 11.4716Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M5.04583 13.8176C5.03756 14.1993 4.71422 14.507 4.33206 14.4966C3.94946 14.4861 3.64399 14.1614 3.65663 13.7797C3.66927 13.4045 3.98563 13.1038 4.36125 13.1095C4.74385 13.1151 5.05368 13.4363 5.04583 13.818V13.8176Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M7.37908 14.497C6.99648 14.4974 6.68143 14.1819 6.6836 13.7997C6.68535 13.4176 7.00389 13.1051 7.38605 13.1094C7.76168 13.1138 8.0702 13.4228 8.07281 13.798C8.07586 14.1801 7.76168 14.4965 7.37908 14.497Z"
                              fill="#CF4520"
                            />
                            <path
                              d="M14.3002 13.354C14.4379 13.354 14.5477 13.3479 14.6571 13.3553C15.0323 13.381 15.316 13.6879 15.3103 14.0569C15.3051 14.4191 15.0231 14.7229 14.654 14.7363C14.2897 14.7498 13.9241 14.7503 13.5598 14.7355C13.2047 14.7211 12.9249 14.4391 12.9158 14.0831C12.9031 13.586 12.9031 13.0881 12.9153 12.5915C12.9245 12.2146 13.2356 11.9286 13.6056 11.9277C13.9751 11.9273 14.2815 12.2128 14.2985 12.5893C14.3098 12.8361 14.3006 13.0842 14.3006 13.354H14.3002Z"
                              fill="#CF4520"
                            />
                          </svg>
                        </Box>
                        <Box
                          marginLeft="8.7px"
                          fontWeight={{ base: '400', md: '500' }}
                        >
                          {programData.date}
                        </Box>
                      </Flex>
                    )}

                    {
                      <Flex my="4.2222px">
                        <Box
                          width="20px"
                          minW="20px"
                          alignItems="start"
                          display="flex"
                        >
                          <svg
                            width="15"
                            height="21"
                            viewBox="0 0 15 21"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M6.9667 20.8334C6.81439 20.6868 6.63353 20.5596 6.51349 20.3903C5.3178 18.6951 4.11313 17.0057 2.9497 15.289C1.98776 13.8693 1.14798 12.3775 0.570492 10.7549C0.20983 9.74147 -0.0165094 8.70334 0.000942005 7.62053C0.0501232 4.57869 2.14641 1.84276 5.08882 0.985445C8.98577 -0.149926 12.9943 2.10347 14.0245 6.00841C14.4169 7.49648 14.2661 8.95722 13.7992 10.4006C13.2889 11.9775 12.521 13.433 11.6072 14.807C10.5458 16.4023 9.42893 17.9608 8.33637 19.5356C8.13806 19.8216 7.95138 20.1165 7.7372 20.3898C7.60817 20.5548 7.4363 20.6862 7.28347 20.8329H6.9667V20.8334ZM7.11953 19.1845C7.15443 19.1446 7.17294 19.1272 7.18775 19.1062C8.28137 17.5398 9.39614 15.9871 10.4612 14.4012C11.3629 13.0588 12.1387 11.6437 12.6495 10.101C13.0366 8.93094 13.2074 7.73985 12.9271 6.52038C12.0715 2.79783 8.12907 0.850883 4.62768 2.4178C2.26434 3.47538 0.851301 6.09619 1.25215 8.64815C1.4235 9.74094 1.79844 10.7628 2.27491 11.7531C3.06287 13.3904 4.07347 14.8937 5.11843 16.3755C5.77683 17.3085 6.44368 18.2357 7.12006 19.1845H7.11953Z"
                              fill="#676055"
                            />
                            <path
                              d="M7.1427 4.22853C9.10255 4.23484 10.6949 5.83277 10.6875 7.78656C10.6806 9.73456 9.07241 11.3172 7.10727 11.3104C5.14743 11.3036 3.55565 9.70565 3.56252 7.75186C3.56887 5.80439 5.17704 4.22223 7.1427 4.22853ZM7.12525 5.40911C5.81851 5.40911 4.74816 6.47405 4.74975 7.77394C4.75133 9.06332 5.81798 10.1267 7.11415 10.1304C8.423 10.134 9.49759 9.07331 9.49917 7.77499C9.50129 6.47457 8.43199 5.40964 7.12472 5.40911H7.12525Z"
                              fill="#676055"
                            />
                          </svg>
                        </Box>
                        <Box color="#676055" marginLeft="8.7px">
                          {parseInt(programData?.is_online)
                            ? data?.json?.onlineProgram
                            : null}

                          {!parseInt(programData?.is_online) &&
                          programData?.address
                            ? parse(programData.address)
                            : null}

                          {!parseInt(programData?.is_online) &&
                          programData?.country &&
                          programData?.address
                            ? `, ${programData?.country}`
                            : null}
                        </Box>
                      </Flex>
                    }

                    {programData?.language &&
                      programData?.language?.toLowerCase() !== 'unspecified' &&
                      programData?.language !== '0' && (
                        <Flex align="center" my="4.2222px">
                          <Box
                            width="20px"
                            minW="20px"
                            alignItems="start"
                            display="flex"
                          >
                            <svg
                              width="20"
                              height="19"
                              viewBox="0 0 20 19"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M7.27773 10.8513C6.53501 10.8513 5.81901 10.8484 5.10359 10.8564C5.04217 10.857 4.96483 10.9175 4.92388 10.9728C4.59915 11.409 4.28182 11.8504 3.96164 12.2901C3.68696 12.6675 3.31787 12.807 2.92433 12.6825C2.516 12.5534 2.29023 12.2261 2.28568 11.7479C2.28284 11.4609 2.28568 11.1739 2.28568 10.8697C2.21516 10.857 2.15601 10.8426 2.09573 10.8357C0.867909 10.6939 0.021114 9.77255 0.0108774 8.51926C-0.00390877 6.7001 -0.00334007 4.88095 0.0108774 3.06237C0.021114 1.70131 1.01293 0.725759 2.35961 0.724607C5.03535 0.722302 7.71051 0.721726 10.3862 0.724607C11.7403 0.726336 12.7225 1.72091 12.7276 3.0906C12.731 3.98203 12.7282 4.87346 12.7282 5.76546C12.7282 5.84267 12.7282 5.91931 12.7282 6.01957C12.8339 6.01957 12.9175 6.01957 13.0006 6.01957C14.5548 6.01957 16.1091 6.01496 17.6633 6.0213C18.811 6.02591 19.6999 6.73756 19.9398 7.83873C19.9808 8.02658 19.9956 8.22365 19.9961 8.41669C20.0001 10.1926 20.0018 11.9686 19.9973 13.7445C19.9939 14.9707 19.233 15.9042 18.0819 16.1088C17.9716 16.1284 17.859 16.1341 17.7202 16.1491C17.7202 16.4338 17.714 16.7121 17.7213 16.9898C17.7333 17.4283 17.5712 17.7614 17.1657 17.9435C16.7938 18.1106 16.4349 18.0138 16.0903 17.6652C15.6331 17.203 15.1815 16.7351 14.7163 16.2816C14.6356 16.2033 14.4991 16.1433 14.3882 16.1428C12.7987 16.1341 11.2086 16.1405 9.61907 16.1353C8.48565 16.1318 7.59677 15.4415 7.34427 14.3691C7.29707 14.1686 7.28171 13.9571 7.28 13.7503C7.27318 12.8732 7.27716 11.9962 7.27716 11.1186V10.8524L7.27773 10.8513ZM16.7631 17.0716C16.7807 17.0618 16.7983 17.0515 16.816 17.0417V16.0166C16.816 15.2911 16.8871 15.2196 17.6093 15.2185C18.5226 15.2173 19.0817 14.6544 19.0828 13.7284C19.0845 11.9599 19.0845 10.1915 19.0828 8.42303C19.0817 7.4976 18.5226 6.93635 17.607 6.93578C14.9603 6.93578 12.313 6.93578 9.66627 6.93578C8.74668 6.93578 8.1831 7.50797 8.18253 8.44319C8.1814 10.1972 8.1814 11.9513 8.18253 13.7053C8.1831 14.6521 8.74555 15.2179 9.68333 15.2179C11.3365 15.2179 12.9903 15.2208 14.6435 15.215C14.8665 15.2145 15.0365 15.2848 15.1901 15.4467C15.5046 15.7786 15.8321 16.0972 16.1512 16.4251C16.3582 16.6377 16.5595 16.8561 16.7631 17.0716ZM11.8086 6.01957C11.8165 5.95273 11.8228 5.9245 11.8228 5.89626C11.8234 4.94721 11.8256 3.99816 11.8205 3.04911C11.8199 2.91428 11.7978 2.77598 11.7619 2.64575C11.5879 2.01882 11.0641 1.64196 10.3652 1.64139C8.00908 1.63966 5.65295 1.64081 3.29626 1.64081C2.96243 1.64081 2.62917 1.6339 2.29535 1.64254C1.52305 1.66328 0.933309 2.2205 0.92876 2.97709C0.916817 4.85329 0.919092 6.73007 0.92876 8.60684C0.932172 9.21534 1.34562 9.72818 1.93195 9.87051C2.14862 9.92295 2.38122 9.91661 2.60699 9.9218C3.02613 9.93102 3.18878 10.0889 3.18934 10.5078C3.18991 10.9146 3.18934 11.3209 3.18934 11.7813C3.26953 11.6799 3.31787 11.6222 3.36166 11.5617C3.69037 11.1111 4.02363 10.6634 4.34437 10.2065C4.48598 10.0054 4.66171 9.91892 4.90284 9.92064C5.61258 9.92698 6.32231 9.92295 7.03205 9.92295C7.10769 9.92295 7.18333 9.92295 7.27716 9.92295C7.27716 9.43834 7.28854 8.97966 7.27375 8.52156C7.25839 8.05597 7.35962 7.61803 7.57175 7.17376C7.45062 7.16857 7.38123 7.16281 7.31242 7.16281C6.6806 7.16224 6.04934 7.16742 5.41751 7.15878C5.26737 7.15647 5.19174 7.20372 5.13202 7.34433C4.93582 7.80588 4.72427 8.26053 4.51726 8.7169C4.43252 8.90418 4.2875 9.01078 4.08163 9.00214C3.88657 8.99407 3.72222 8.89265 3.68411 8.70307C3.65511 8.56132 3.67899 8.38557 3.73814 8.25189C4.45413 6.63383 5.18321 5.02212 5.90318 3.4058C5.99815 3.1926 6.12099 3.02664 6.36667 3.02607C6.61235 3.02491 6.7386 3.18914 6.83301 3.40234C7.25385 4.35197 7.68151 5.29814 8.10746 6.24546C8.13874 6.31518 8.17514 6.38202 8.21552 6.46385C8.71029 6.09276 9.25738 5.99883 9.83973 6.01554C10.3857 6.0311 10.9328 6.019 11.4793 6.019H11.8074L11.8086 6.01957ZM6.36895 4.59687C6.11303 5.17021 5.87816 5.69516 5.63817 6.23336H7.10428C6.85917 5.68824 6.62316 5.16272 6.36895 4.59687Z"
                                fill="#676055"
                              />
                              <path
                                d="M13.204 13.2162C12.9271 12.7926 12.6449 12.3914 12.3979 11.9695C12.2621 11.7371 12.3726 11.4633 12.5955 11.3471C12.8289 11.2261 13.0893 11.3123 13.2322 11.5595C13.2687 11.6232 13.2928 11.6952 13.3357 11.7536C13.5685 12.0692 13.806 12.3819 14.053 12.7105C14.7267 12.1359 15.093 11.4645 15.2359 10.6296H14.9878C13.9536 10.6296 12.9194 10.6296 11.8852 10.6296C11.8047 10.6296 11.7218 10.6326 11.6448 10.6149C11.4196 10.5642 11.265 10.3494 11.2826 10.1264C11.3014 9.88743 11.4819 9.70158 11.7312 9.69686C12.2662 9.68742 12.8018 9.69214 13.3374 9.69155C13.4239 9.69155 13.5109 9.69155 13.6332 9.69155C13.6332 9.36647 13.6273 9.06026 13.6349 8.75464C13.642 8.47322 13.8225 8.28914 14.0818 8.28147C14.3587 8.2738 14.5592 8.4685 14.5656 8.76644C14.5721 9.06616 14.5668 9.36647 14.5668 9.69155C14.8866 9.69155 15.1853 9.69155 15.484 9.69155C15.7991 9.69155 16.1148 9.68683 16.43 9.69391C16.7228 9.70099 16.9079 9.88802 16.9079 10.1588C16.9079 10.4267 16.7169 10.6161 16.43 10.6291C16.3571 10.6326 16.2842 10.6291 16.1877 10.6291C16.0572 11.6114 15.6568 12.4592 14.9443 13.1796C15.3064 13.3625 15.6762 13.4215 16.0531 13.4528C16.126 13.4587 16.2007 13.4516 16.273 13.4622C16.5311 13.4988 16.7039 13.7135 16.6887 13.9725C16.674 14.2227 16.4893 14.3908 16.2165 14.392C15.5363 14.3944 14.8943 14.2327 14.2946 13.9147C14.1535 13.8398 14.053 13.8445 13.9154 13.9183C13.3298 14.2304 12.7025 14.3908 12.0375 14.392C11.7177 14.3926 11.4943 14.1938 11.4966 13.9112C11.4984 13.6457 11.7024 13.4622 12.0122 13.4557C12.4338 13.4469 12.8377 13.3601 13.2028 13.2156L13.204 13.2162Z"
                                fill="#676055"
                              />
                            </svg>
                          </Box>
                          <Box
                            color="#676055"
                            textTransform="capitalize"
                            marginLeft="8.7px"
                          >
                            {card?.id == '12733'
                              ? data?.json?.IEOLanguages
                              : programData.language}
                          </Box>
                        </Flex>
                      )}
                    {programData?.amount &&
                      programData.amount.length === 1 &&
                      programData.amount[0]?.amount > 0 && (
                        <Flex
                          align="center"
                          color="#28231E"
                          fontWeight="500"
                          my="4.2222px"
                        >
                          <Box
                            width="20px"
                            minW="20px"
                            alignItems="start"
                            display="flex"
                          >
                            <svg
                              width="20"
                              height="12"
                              viewBox="0 0 20 12"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.74277 0.000455312C11.3606 0.000455312 13.9794 -0.00056914 16.5973 0.000455312C17.3243 0.000455312 17.481 0.158221 17.481 0.904022C17.483 3.83088 17.483 6.75671 17.481 9.68357C17.481 10.3413 17.3192 10.5113 16.6862 10.5124C11.3828 10.5144 6.07944 10.5134 0.776045 10.5103C0.161276 10.5103 0.00252784 10.3433 0.0015167 9.72148C-0.000505567 6.76081 -0.000505567 3.80015 0.0015167 0.839481C0.0015167 0.16949 0.166332 0.00250422 0.837725 0.00250422C3.47274 0.000455312 6.10776 0.000455312 8.74277 0.000455312ZM16.577 7.17776C16.577 5.97096 16.5821 4.7826 16.5689 3.59423C16.5679 3.49179 16.4648 3.3248 16.3768 3.30021C15.1776 2.95805 14.4759 2.14361 14.1837 0.955244C14.1089 0.936804 14.0603 0.914266 14.0118 0.914266C10.5385 0.911193 7.06429 0.906071 3.59104 0.923486C3.4596 0.923486 3.25838 1.11608 3.20985 1.25951C2.8701 2.26654 2.22399 2.93961 1.21589 3.25104C1.10871 3.28382 1.0076 3.33607 0.916593 3.37397V7.18903C2.00356 7.45129 2.79933 8.05982 3.13806 9.13856C3.26141 9.53195 3.43735 9.61493 3.80844 9.61289C7.09766 9.59854 10.3869 9.60469 13.6761 9.60469C13.8409 9.60469 14.0057 9.60469 14.1796 9.60469C14.4921 8.28315 15.2868 7.4933 16.576 7.17674L16.577 7.17776Z"
                                fill="#28231E"
                              />
                              <path
                                d="M9.69804 11.988C7.23087 11.988 4.76371 11.988 2.29654 11.988C2.1297 11.988 1.92545 12.0321 1.80412 11.9532C1.64537 11.8508 1.47044 11.652 1.4573 11.482C1.4482 11.3611 1.67368 11.1818 1.83142 11.0988C1.95073 11.0353 2.12667 11.0773 2.27834 11.0773C7.16212 11.0773 12.0469 11.0773 16.9307 11.0763C17.9974 11.0763 18.138 10.9308 18.138 9.83055C18.138 7.41489 18.138 4.99821 18.138 2.58255C18.138 2.4463 18.0905 2.27932 18.1511 2.18097C18.2593 2.00374 18.4322 1.86749 18.5778 1.71484C18.7275 1.86441 18.9479 1.99042 19.0076 2.17072C19.0834 2.40123 19.0288 2.67475 19.0288 2.92984C19.0288 5.3455 19.0308 7.76218 19.0278 10.1778C19.0258 11.3334 18.3918 11.9829 17.2512 11.986C14.7335 11.9921 12.2158 11.988 9.69804 11.988Z"
                                fill="#28231E"
                              />
                              <path
                                d="M5.41704 5.26171C5.42209 3.37262 6.8761 1.90049 8.73659 1.89844C10.5839 1.89741 12.0683 3.39721 12.0642 5.26376C12.0612 7.12724 10.5657 8.63933 8.73356 8.63421C6.88318 8.62909 5.41198 7.13339 5.41602 5.26171H5.41704ZM6.31391 5.25249C6.31189 6.61604 7.38672 7.71937 8.72648 7.72962C10.046 7.73884 11.1653 6.61296 11.1704 5.27196C11.1754 3.92275 10.0804 2.80815 8.74468 2.80405C7.39886 2.79996 6.31492 3.891 6.3129 5.25249H6.31391Z"
                                fill="#28231E"
                              />
                            </svg>
                          </Box>
                          <Box
                            color="#28231E"
                            fontWeight="600"
                            marginLeft="8.7px"
                          >
                            {programData.amount[0]?.amount}{' '}
                            {programData.currency}
                          </Box>
                        </Flex>
                      )}
                    <Flex mt="1rem">
                      <Box color="#28231E">
                        {programData.popup_description?.length > 315 &&
                          `${parse(
                            programData.popup_description?.substr(0, 314)
                          )}...`}
                        {programData.popup_description?.length <= 315 &&
                          parse(programData.popup_description)}
                      </Box>
                    </Flex>
                  </Flex>
                )}
                {!programData?.title && (
                  <Flex
                    flexDir="column"
                    gridGap="8.5px"
                    gap="8.5px"
                    p="50px"
                    textAlign="center"
                  >
                    No Details Available
                  </Flex>
                )}
              </ModalBody>
              {programData?.title && (
                <Flex justifyContent="center" align="center">
                  <ModalFooter
                    p={{ base: '0 0 30px 0', md: '0 0 30px 0' }}
                    gap="14px"
                    gridGap="14px"
                  >
                    <SimpleButton
                      style={{
                        buttonStyle: 'White with Orange outline',
                        buttonText: data?.json?.knowMoreButton,
                        linkUrl:
                          card.id == 12733
                            ? 'https://isha.sadhguru.org/in/en/inner-engineering'
                            : programData?.landing_url ||
                              `program-details?id=${card.id}`,
                      }}
                      target="_blank"
                      mb="0px"
                      mt="16px"
                      region={region}
                      lang={lang}
                      clickHandler={() => {
                        pushIntoDataLayer('pf_know_more_clicks', {
                          program_name: card?.name,
                        });
                      }}
                    />
                    {programData?.registration_enabled != 0 &&
                      programData?.register_url &&
                      card?.flag.indexOf('regClosed') < 0 && (
                        <SimpleButton
                          clickHandler={() => {
                            pushIntoDataLayer('pf_register_clicks', {
                              program_name: card?.name,
                            });
                          }}
                          style={{
                            buttonStyle: 'Orange',
                            buttonText: data?.json?.registerButton,
                            linkUrl: programData.register_url,
                          }}
                          mb="0px"
                          mt="16px"
                          region={region}
                          lang={lang}
                        />
                      )}
                  </ModalFooter>
                </Flex>
              )}
            </>
          )}
          {!loading && apiStatus !== 200 && (
            <ModalBody
              maxH="400px"
              overflowY="auto"
              pl={{ base: '20px', md: '35px' }}
            >
              <Flex
                flexDir="column"
                gridGap="8.5px"
                gap="8.5px"
                p="50px"
                textAlign="center"
              >
                Something Went Wrong. Please try again after some time.
              </Flex>
            </ModalBody>
          )}
          {loading && !programData?.title && (
            <Loader left="45%" mt="0" top="40%" />
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
};
ProgramCard.whyDidYouRender = true;
export default React.memo(ProgramCard);
